import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import * as R from 'ramda';

import {
  Answer,
  AnswerWithExtraInput,
  AnswerWithExtraInputDict,
  Question,
} from '../types';
import { dictToArrayByOrder } from '../utils';

type AnswersComponentProps = {
  answers: AnswerWithExtraInput[];
  selectedAnswers: Answer['id'][];
  toggleAnswer: (id: Answer['id']) => void;
  updateExtraAnswerInput: (
    id: Answer['id'],
    extraInput: AnswerWithExtraInput['extraInput'],
  ) => void;
};

const CheckboxAnswers: FC<AnswersComponentProps> = ({
  answers,
  selectedAnswers,
  toggleAnswer,
  updateExtraAnswerInput,
}) => (
  <>
    {answers.map(a => {
      const isChecked = selectedAnswers.includes(a.id);
      return (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={() => toggleAnswer(a.id)}
              />
            }
            label={a.content}
          />
          {a.needsInput && (
            <TextField
              multiline
              fullWidth
              disabled={!isChecked}
              value={a.extraInput ?? ''}
              onChange={e => updateExtraAnswerInput(a.id, e.target.value)}
            />
          )}
        </Grid>
      );
    })}
  </>
);

const RadioButtonAnswers: FC<AnswersComponentProps> = ({
  answers,
  selectedAnswers,
  toggleAnswer,
  updateExtraAnswerInput,
}) => (
  <RadioGroup
    aria-labelledby="answers"
    value={selectedAnswers[0] ?? ''}
    onChange={e => toggleAnswer(e.target.value)}
    name="answers"
  >
    {answers.map(a => (
      <>
        <FormControlLabel value={a.id} control={<Radio />} label={a.content} />
        {a.needsInput && (
          <TextField
            multiline
            fullWidth
            disabled={selectedAnswers[0] !== a.id}
            value={a.extraInput ?? ''}
            onChange={e => updateExtraAnswerInput(a.id, e.target.value)}
          />
        )}
      </>
    ))}
  </RadioGroup>
);

const FormFeedbackQuestion: FC<{
  question: Question;
  feedbackState?: AnswerWithExtraInputDict;
  updateFeedback: (
    qid: Question['id'],
    newAnswers: AnswerWithExtraInputDict,
  ) => void;
}> = ({ question, feedbackState = {}, updateFeedback }) => {
  const [answers, setAnswers] = useState<AnswerWithExtraInputDict>(
    R.mergeDeepRight(question.answers, feedbackState),
  );
  useEffect(() => {
    const a = R.mergeDeepRight(answers, feedbackState);
    setAnswers(a);
  }, [feedbackState]);

  const selectedA = useMemo(() => Object.keys(feedbackState), [feedbackState]);

  const toggleAnswer = (id: Answer['id']) => {
    // remove already existing
    if (feedbackState?.[id]) {
      updateFeedback(question.id, R.dissoc(id, feedbackState));
      // add new if multi answers
    } else if (question.multiOptions) {
      updateFeedback(question.id, R.assoc(id, answers[id], feedbackState));
      // replace existing if single answer
    } else {
      updateFeedback(question.id, R.assoc(id, answers[id], {}));
    }
  };

  const updateExtraAnswerInput = (
    id: Answer['id'],
    extraInput: AnswerWithExtraInput['extraInput'],
  ) => {
    const updatedAnswer = R.assocPath(
      [id, 'extraInput'],
      extraInput,
      feedbackState,
    );
    updateFeedback(question.id, updatedAnswer);
  };

  return (
    <Grid container spacing={2} style={{ padding: '1em' }}>
      <Grid item xs={12}>
        <Typography variant="h6">{question.content}</Typography>
      </Grid>
      {question.multiOptions ? (
        <CheckboxAnswers
          answers={dictToArrayByOrder(answers)}
          selectedAnswers={selectedA}
          toggleAnswer={toggleAnswer}
          updateExtraAnswerInput={updateExtraAnswerInput}
        />
      ) : (
        <RadioButtonAnswers
          answers={dictToArrayByOrder(answers)}
          selectedAnswers={selectedA}
          toggleAnswer={toggleAnswer}
          updateExtraAnswerInput={updateExtraAnswerInput}
        />
      )}
    </Grid>
  );
};

export default FormFeedbackQuestion;
